import { useEffect, useMemo, useState } from "react";
import { Bell, Check, X, CircleCheck, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Chief-of-Staff inbox (intelligence programme, Phase B).
 *
 * A bell in the app header with an unread badge. Opening it lists the
 * insights addressed to the current user (server hard-scopes to the
 * recipient) and lets them resolve each: Done / Dismiss.
 *
 * - Opening marks the visible `new` items as `seen` (clears the badge
 *   without forcing a decision — same as any notification tray).
 * - Deep link: `/?insights=1` auto-opens (the push notification's
 *   tap-through target).
 */

const SEV_DOT: Record<string, string> = {
  info: "bg-sky-500",
  nudge: "bg-coral",
  warn: "bg-amber-500",
  critical: "bg-red-600",
};

export function NudgeInbox() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();

  const unreadQ = trpc.insights.unreadCount.useQuery(undefined, {
    refetchInterval: 90_000,
    staleTime: 60_000,
  });
  const listQ = trpc.insights.list.useQuery(
    { limit: 30 },
    { enabled: open, staleTime: 15_000 },
  );
  const markSeen = trpc.insights.markSeen.useMutation();
  const setStatus = trpc.insights.setStatus.useMutation();

  // Auto-open from the push deep link (?insights=1), once.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    if (p.get("insights") === "1") {
      setOpen(true);
      p.delete("insights");
      const qs = p.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (qs ? `?${qs}` : ""),
      );
    }
  }, []);

  const items = listQ.data ?? [];
  const newIds = useMemo(
    () => items.filter(i => i.status === "new").map(i => i.id),
    [items],
  );

  // On open: clear the "new" badge by marking them seen.
  // `listQ.isError` is checked explicitly, not left to the fact that `items`
  // happens to be empty on failure: marking rows seen because a request failed
  // would silently clear a badge the user never actually saw behind.
  useEffect(() => {
    if (!open || listQ.isError || newIds.length === 0) return;
    markSeen.mutateAsync({ ids: newIds }).then(() => {
      utils.insights.unreadCount.invalidate();
      utils.insights.list.invalidate();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, newIds.join(",")]);

  async function resolve(
    id: number,
    status: "actioned" | "dismissed" | "acknowledged",
  ) {
    await setStatus.mutateAsync({ id, status });
    utils.insights.list.invalidate();
    utils.insights.unreadCount.invalidate();
  }

  const count = unreadQ.data?.count ?? 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative rounded-full p-2 hover:bg-muted text-foreground/70 hover:text-foreground transition-colors"
          aria-label={isAr ? "التنبيهات" : "Insights"}
          title={isAr ? "التنبيهات" : "Insights"}
        >
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span className="absolute -top-0.5 -end-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-coral text-white text-[10px] font-medium grid place-items-center">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(92vw,380px)] p-0 max-h-[70vh] overflow-y-auto"
      >
        <div className="px-3 py-2.5 border-b border-border/60 flex items-center gap-2">
          <Bell className="h-4 w-4 text-coral" />
          <span className="text-sm font-medium flex-1">
            {isAr ? "ما يستحق انتباهك" : "Worth your attention"}
          </span>
        </div>

        {listQ.isLoading && (
          <div className="p-6 grid place-items-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}

        {/*
          A FAILED LOAD IS NOT AN EMPTY INBOX.

          `items` is `listQ.data ?? []`, so a failed request produces an empty
          array and — before this branch existed — fell straight through to the
          all-clear copy below. A network blip, an expired session or a 500 all
          told the user, confidently, that nothing needed their attention. That
          is the one failure mode an inbox must not have, because it is
          indistinguishable from the truth.

          `role="status"` (polite), not `alert`: the badge query refetches on a
          timer, and an assertive region would re-interrupt a screen reader
          every time a background retry failed.
        */}
        {!listQ.isLoading && listQ.isError && (
          <div role="status" className="p-6 text-center space-y-2">
            <p className="text-sm text-foreground">
              {isAr ? "تعذّر تحميل الإشعارات." : "Couldn't load notifications."}
            </p>
            <p className="text-[12px] text-muted-foreground">
              {isAr
                ? "تحقّق من اتصالك وحاول مرة أخرى."
                : "Check your connection and try again."}
            </p>
            <button
              type="button"
              onClick={() => listQ.refetch()}
              className="inline-flex items-center gap-1 text-[12px] rounded-full border border-border px-3 py-1 hover:bg-muted transition-colors"
            >
              {isAr ? "إعادة المحاولة" : "Try again"}
            </button>
          </div>
        )}

        {!listQ.isLoading && !listQ.isError && items.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {isAr
              ? "لا شيء الآن، كل شيء على ما يرام 👌"
              : "Nothing right now, you're all clear 👌"}
          </div>
        )}

        <ul className="divide-y divide-border/50">
          {items.map(i => (
            <li key={i.id} className="px-3 py-2.5">
              <div className="flex items-start gap-2">
                <span
                  className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                    SEV_DOT[i.severity] ?? "bg-coral"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-snug">
                    {i.title}
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                    {i.body}
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <button
                      type="button"
                      onClick={() => resolve(i.id, "actioned")}
                      disabled={setStatus.isPending}
                      className="inline-flex items-center gap-1 text-[11px] rounded-full bg-emerald-600 text-white px-2 py-1 hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <CircleCheck className="h-3 w-3" />
                      {isAr ? "تمّت المعالجة" : "Done"}
                    </button>
                    <button
                      type="button"
                      onClick={() => resolve(i.id, "dismissed")}
                      disabled={setStatus.isPending}
                      className="inline-flex items-center gap-1 text-[11px] rounded-full border border-border px-2 py-1 hover:bg-muted disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                      {isAr ? "تجاهل" : "Dismiss"}
                    </button>
                    {i.status === "acknowledged" && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Check className="h-3 w-3" />
                        {isAr ? "تم الاطلاع" : "Acknowledged"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
